import FailedGenerationDialog from 'components/Grading/FailedGenerationDialog';
import PushRubricDialog from 'components/InstructorAssignments/PushRubricDialog';
import FineTuneDialog from 'components/Preview/FineTuneDialog';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import Desktop from 'pages/Preview/desktop';
import Mobile from 'pages/Preview/mobile';
import { PageContext } from 'pages/Preview/PageProvider';
import Tablet from 'pages/Preview/tablet';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJoyride from 'react-joyride';
import { IconButton, Loading, Stack, styled, Text } from 'shell/components';
import theme from 'shell/themes';

const joyrideStyles = {
    options: {
        borderRadius: '8px',
        arrowColor: theme.palette.ensightful.primary[500],
        backgroundColor: theme.palette.ensightful.primary[500],
        textColor: theme.palette.ensightful.shades[0],
        zIndex: theme.zIndex.tooltip + 1,
    },
};

const Tooltip = ({ step, closeProps, tooltipProps }) => {
    return (
        <TooltipRootStack {...tooltipProps}>
            <CloseIconButton {...closeProps} kind={'exit'} size={'small'} variant={'square'} />
            {step.content}
        </TooltipRootStack>
    );
};

const PageLayout = () => {
    const { isLoading } = useApp();
    const { currentUser, updateCurrentUser } = useAuth();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const contextProps = useContext(PageContext);

    const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);

    const {
        currentAssignment,
        currentSubmission,
        showFailedDialog,
        setShowFailedDialog,
        pushRubricDialog,
        setPushRubricDialog,
        fineTuneDialog,
        setIsApplyingChanges,
    } = contextProps;

    const joyrideSteps = [
        {
            target: '.joyride-step-1',
            content: (
                <Stack alignItems={'flex-start'} spacing={2}>
                    <Text color={'inherit'} component={'p'} variant={'h6'}>
                        {t('fine-tuning.fine-tune-assignment', 'Fine-Tune Your Assignment')}
                    </Text>
                    <Text color={'inherit'} weight={'regular'}>
                        {t(
                            'fine-tuning.fine-tune-description',
                            'If grading/feedback doesn’t look quite right, you can fine-tune assignment details such as the rubric, grading instructions, and feedback instructions to enhance the accuracy of AI grading and/or feedback suggestions.'
                        )}
                    </Text>
                </Stack>
            ),
            disableBeacon: true,
            disableOverlay: true,
            placement: device === 'mobile' ? 'auto' : 'top-end',
        },
    ];

    const joyrideCallback = (data) => {
        const { action } = data;
        if (action === 'close') {
            setShowOnboardingTooltip(false);
        }
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...contextProps} />,
            tablet: <Tablet {...contextProps} />,
            desktop: <Desktop {...contextProps} />,
        }[device];
    };

    const closePushRubricDialog = () => {
        setIsApplyingChanges(false);
        setPushRubricDialog({ open: false });
    };

    useEffect(() => {
        if (!currentUser.has_seen_preview_onboarding) {
            setShowOnboardingTooltip(true);
            updateCurrentUser({ has_seen_preview_onboarding: true });
        }
    }, [currentUser, updateCurrentUser]);

    useEffect(() => {
        if (fineTuneDialog.open) setShowOnboardingTooltip(false);
    }, [fineTuneDialog.open]);

    return isLoading || !currentAssignment || !currentSubmission ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FailedGenerationDialog open={showFailedDialog} onClose={() => setShowFailedDialog(false)} />
            <FineTuneDialog />
            <ReactJoyride
                callback={joyrideCallback}
                disableScrollParentFix
                run={showOnboardingTooltip}
                stepIndex={0}
                steps={joyrideSteps}
                styles={joyrideStyles}
                tooltipComponent={Tooltip}
            />
            <PushRubricDialog
                open={pushRubricDialog.open}
                form={pushRubricDialog.form}
                onClose={closePushRubricDialog}
                onSave={pushRubricDialog.onSave}
            />
        </>
    );
};

const TooltipRootStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        borderRadius: '8px',
        maxWidth: '393px',
        padding: theme.spacing(4, 5),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

const CloseIconButton = styled(IconButton)(({ theme }) => {
    return {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(4),
        color: theme.palette.ensightful.shades[0],
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.ensightful.secondary[100],
        },
    };
});

export default PageLayout;
